import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import { AuthTags } from '~constants/authTags';

import { formatDateString } from '~libs/dayjs';
import { ToastType, useToast } from '~libs/toast';
import { formatSahMessages, parseSahApiError, severityOf } from '~support-at-home/lib/sahApiError';

import useEditableTableRows, { EditableRow } from '~support-at-home/hooks/useEditableTableRows';

import { Button, ErrorResponse } from '~components';
import { Row, TableView } from '~components/table-view';
import DestructiveConfirmDialog from '~support-at-home/components/DestructiveConfirmDialog';

import columnsFactory, { ORIGIN_TAG_API } from '~support-at-home/forms/sah-budgets/listSingle';

import { create, deleteOne, electToReturn, getOne, rollover, update } from '~support-at-home/actions/sahBudgets';

import { clearError } from '~support-at-home/reducers/sahBudgets';

import { selectUserTags } from '~main/selectors/login';
import { selectSahBudgetsByClient, selectSahConnected } from '~support-at-home/selectors/sahBudgets';

import { filterBudgetRowFields } from '../helpers';

interface ComponentProps {
  id: number | null;
  /** The clicked list row's client name, for naming the budget in the rollover / elect-to-return dialogs. */
  clientName?: string | null;
  isOpen?: boolean;
  onClose: (confirm: boolean) => void;
}

type PendingAction = { kind: 'rollover' | 'electToReturn'; row: Row } | null;

// A 200 is still a success even when it carries an 'E' message - the vendor call completed. The
// severity only chooses how loudly the toast says so.
const toastTypeForSeverity = (severity: 'E' | 'W' | null): ToastType => {
  if (severity === 'E') return 'danger';
  if (severity === 'W') return 'info';
  return 'success';
};

// Only reached when the vendor returned neither messages nor a message body.
const SUCCESS_FALLBACK: Record<NonNullable<PendingAction>['kind'], string> = {
  rollover: 'Budget rolled over successfully.',
  electToReturn: 'Unspent funds elected for return successfully.',
};

// Names the dialog for screen readers (aria-labelledby on the dialog points at the header).
const MODAL_HEADER_ID = 'client-budgets-modal-title';

const BudgetsModal: React.FC<ComponentProps> = ({ id, clientName = null, isOpen = false, onClose }) => {
  const dispatch = useAppDispatch();
  // `clientRows` is only replaced on getOne.fulfilled, and this modal is never unmounted, so on
  // reopening for a different client it still holds the PREVIOUS client's budgets until the new
  // fetch resolves. Rows for any other client are dropped here, so neither the table, the read-only
  // note nor the rollover / elect-to-return dialogs (irreversible) can act on or name the wrong
  // client's budget in that window. Filtered rather than cleared on getOne.pending, which would
  // blank the table on every same-client reload after a save or an action.
  const loadedClientBudgets = useAppSelector(selectSahBudgetsByClient);
  const clientBudgets = useMemo(
    () => loadedClientBudgets.filter((budget) => budget.client_id === id),
    [loadedClientBudgets, id],
  );
  const saConnected = useAppSelector(selectSahConnected);
  const userTags = useAppSelector(selectUserTags);
  const canElectToReturn = userTags.includes(AuthTags.CLIENTS.BUDGETS.ELECTTORETURN);
  const { showToast } = useToast();

  const persistedBudgets: EditableRow[] = useMemo(
    () => clientBudgets.map((budget) => ({ ...budget, persisted: true, backendId: budget.id })),
    [clientBudgets],
  );

  const getDefaultRow = useCallback(
    (): EditableRow => ({
      id: crypto.randomUUID(),
      client_id: id,
      sah_budget_id: '',
      origin_tag: 'manual',
      start_date: '',
      end_date: '',
      prior_spent_amount: null,
      prior_spent_date: '',
      prior_spent_included_in_statements: false,
    }),
    [id],
  );

  const load = useCallback(() => {
    dispatch(getOne({ client_id: id }));
  }, [dispatch, id]);

  useEffect(() => {
    if (isOpen && id !== null) {
      load();
    }
  }, [id, isOpen, load]);

  const [action, setAction] = useState<PendingAction>(null);
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const openRolloverDialog = useCallback((row: Row) => setAction({ kind: 'rollover', row }), []);
  const openElectToReturnDialog = useCallback((row: Row) => setAction({ kind: 'electToReturn', row }), []);

  // `columns` is referenced inside onCreate/onUpdate below via closure - by the time those
  // callbacks actually run (a later user confirm, never synchronously during this render) the
  // `const columns` a few lines down has long since been assigned, so there is no temporal-dead-
  // zone hazard. Declared after the hook so it can depend on the hook's own `isDirty`/
  // `isProcessing` (Decision E1/E3).
  const { rows, tableRef, error, isProcessing, isDirty, handleChange, canRowValidate, confirmChanges } =
    useEditableTableRows({
      source: persistedBudgets,
      getDefaultRow,
      onCreate: async (row) => {
        const toCreate = filterBudgetRowFields(row, columns, id, row.sah_budget_id);
        await dispatch(create(toCreate)).unwrap();
      },
      onUpdate: async (row) => {
        if (!row.backendId) return;
        // TAV-2977: available_amount is a derived, non-editable display column. TableView still
        // writes its deriveValue result onto the row object, so without this exclusion it would be
        // silently resubmitted on every save of any other field, overwriting the authoritative
        // uploaded figure with a client-computed one. Scoped to update only: a newly created row
        // has no authoritative uploaded value yet, so onCreate is unaffected and still sends
        // whatever TableView derives for a brand-new row.
        const { available_amount, ...toUpdate } = filterBudgetRowFields(row, columns, id);
        void available_amount;
        await dispatch(update({ ...toUpdate, id: row.backendId })).unwrap();
      },
      onDelete: async (row) => {
        if (!row.backendId) return;
        await dispatch(deleteOne({ id: row.backendId })).unwrap();
      },
    });

  const columns = useMemo(
    () =>
      columnsFactory({
        saConnected,
        isDirty,
        isProcessing,
        canElectToReturn,
        onRollover: openRolloverDialog,
        onElectToReturn: openElectToReturnDialog,
      }),
    [saConnected, isDirty, isProcessing, canElectToReturn, openRolloverDialog, openElectToReturnDialog],
  );

  // Both new thunks share the sahBudgets slice prefix, so reduxUtils' global isRejected matcher
  // writes state.error on a failed rollover / elect-to-return. Nothing else clears it: getOne
  // .fulfilled does not touch `error`, so neither this modal's load() nor the parent's
  // onModalClose -> load() resets it, and the parent Budgets.tsx renders it via selectErrorResponse.
  // Left alone, a failed action leaves a stale Services Australia banner on the budgets list after
  // this modal closes. BudgetsModal itself reads `error` from the hook rather than the slice, so
  // clearing here is inert for this component and load-bearing for the parent.
  //
  // Called on EVERY route out of the dialog, not just cancel: a failure followed by a successful
  // retry exits through handleConfirmAction's own setAction(null), which would otherwise strand the
  // error from the first attempt.
  const dismissAction = useCallback(() => {
    setAction(null);
    // `clearError` is a payload-less action, but buildSlice's generic customActions typing
    // (reduxUtils.ts) infers PayloadAction<any> rather than void for every custom action, so the
    // generated creator's type still expects an argument even though the reducer ignores it.
    dispatch(clearError(undefined));
  }, [dispatch]);

  const closeActionDialog = () => {
    if (isSubmittingAction) return;
    dismissAction();
    setDialogError(null);
  };

  // Safety invariant: no parent, no dialog. Please do not remove this as redundant.
  //
  // BudgetsModal is never unmounted - Budgets.tsx renders it unconditionally and only toggles
  // `isOpen` - so `action` outlives a close. Both DestructiveConfirmDialogs are gated on
  // `action?.kind` alone, so without this the dialog would survive the parent closing and resurface
  // on the next open, still armed against the PREVIOUS client's budget row. Today that is latent
  // rather than live only because the outer Modal has no `toggle` prop (reactstrap makes Esc inert
  // without one) and the dialog's own backdrop covers the parent's Cancel button, both of which are
  // accidents of the current markup rather than guarantees. The action behind this dialog is
  // irreversible and moves Commonwealth funds, so the invariant is stated explicitly instead.
  useEffect(() => {
    if (!isOpen && action) {
      dismissAction();
      setDialogError(null);
    }
  }, [action, dismissAction, isOpen]);

  const handleConfirmAction = async () => {
    if (!action) return;
    setIsSubmittingAction(true);
    setDialogError(null);
    try {
      const result =
        action.kind === 'rollover'
          ? await dispatch(rollover({ id: String(action.row.backendId) })).unwrap()
          : await dispatch(electToReturn({ id: String(action.row.backendId), consent_acknowledged: true })).unwrap();

      // A 200 can still carry Warning/Error messages - neither call is fire-and-forget. The
      // vendor call DID complete and the local record is already updated; the message severity
      // only chooses how loudly we say so.
      const messages = formatSahMessages(result?.data?.messages);
      const worst = severityOf(result?.data?.messages);
      const { kind } = action;
      dismissAction();
      // genericAction's sendRequest returns `response.data ?? true`, so an empty-bodied 2xx makes
      // `result` the boolean true and `result.message` undefined - a toast reading "undefined"
      // after an irreversible action, with nothing else confirming it worked. Name what happened
      // rather than falling back to something generic.
      showToast(
        toastTypeForSeverity(worst),
        messages.length ? messages.join(' · ') : (result?.message ?? SUCCESS_FALLBACK[kind]),
      );
      load();
    } catch (err) {
      // Stays open so the user can retry.
      setDialogError(parseSahApiError((err as Error).message));
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const handleConfirm = async () => {
    const outcome = await confirmChanges();
    if (outcome === 'saved') {
      load();
      onClose(true);
    } else if (outcome === 'clean') {
      onClose(false);
    }
  };

  // Names the budget the rollover / elect-to-return dialogs act on. Order: Budget ID, Budget
  // Period, then Client (only when known; see the ComponentProps comment on `clientName`).
  const actionDetails = action
    ? [
        { label: 'Budget ID', value: String(action.row.sah_budget_id ?? '') },
        {
          label: 'Budget Period',
          value: `${formatDateString(action.row.start_date)} - ${formatDateString(action.row.end_date)}`,
        },
        ...(clientName ? [{ label: 'Client', value: clientName }] : []),
      ]
    : [];

  // The read-only note only applies to budgets sourced from Services Australia; a manual-only
  // client has nothing here that refreshes automatically. Checked against the persisted
  // `clientBudgets`, not the in-progress `rows`, since a newly-added row is never `'api'`.
  const hasSaBudget = clientBudgets.some((budget) => budget.origin_tag === ORIGIN_TAG_API);

  return (
    <>
      <Modal
        isOpen={isOpen}
        size="xl"
        style={{ maxWidth: '1800px', width: '100%' }}
        labelledBy={MODAL_HEADER_ID}
        data-cy="client-budgets-modal"
      >
        <ModalHeader className="bg-success text-white" id={MODAL_HEADER_ID}>
          Client SAH Budgets
        </ModalHeader>
        <ModalBody>
          <ErrorResponse message={error} dataCy="client-budgets-modal-error" />
          {hasSaBudget && (
            <p className="text-muted small" data-cy="client-budgets-modal-readonly-note">
              Budgets sourced from Services Australia refresh automatically, so their values cannot be edited here.
              They can still be removed.
            </p>
          )}
          <div style={{ overflowX: 'auto' }}>
            <TableView
              ref={tableRef}
              id="client-budgets-modal"
              columns={columns}
              data={rows}
              editonly
              onChange={handleChange}
              canRowValidate={canRowValidate}
              canDeleteRow
              showAddRow
              emptyMessage="No data found"
              bordered
              compactView
            />
          </div>
        </ModalBody>
        <ModalFooter className="justify-content-end">
          <Button
            size="sm"
            color="success"
            className="ps-4 pe-4 mx-2"
            onClick={handleConfirm}
            loading={isProcessing}
            dataCy="client-budgets-modal-confirm-button"
          >
            Confirm
          </Button>
          <Button
            size="sm"
            className="ps-4 pe-4 mx-2"
            onClick={() => onClose(false)}
            disabled={isProcessing}
            dataCy="client-budgets-modal-cancel-button"
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      <DestructiveConfirmDialog
        isOpen={action?.kind === 'rollover'}
        title="Roll Over Budget"
        details={actionDetails}
        warnings={[
          'This closes the current budget in TurnPoint and requests Services Australia to roll it over early.',
          'This action cannot be undone from TurnPoint once confirmed.',
          'No further claims can be made against this budget once it is closed.',
        ]}
        acknowledgement="I understand this will close the budget and cannot be undone."
        confirmLabel="Roll Over Budget"
        submittingLabel="Rolling over…"
        error={dialogError}
        isSubmitting={isSubmittingAction}
        dataCy="sah-budget-rollover-confirm-dialog"
        onClose={closeActionDialog}
        onConfirm={handleConfirmAction}
      />

      <DestructiveConfirmDialog
        isOpen={action?.kind === 'electToReturn'}
        title="Elect to Return Unspent Funds"
        details={actionDetails}
        warnings={[
          'This closes the current budget in TurnPoint and requests Services Australia to return the unspent Commonwealth funds.',
          'This action cannot be undone from TurnPoint once confirmed.',
          "Under s226B of the Aged Care Act 2024, the provider must hold the care recipient's written agreement before electing to return unspent funds.",
        ]}
        acknowledgement="I confirm the care recipient's written agreement has been obtained and understand this cannot be undone."
        confirmLabel="Elect to Return Funds"
        submittingLabel="Submitting…"
        error={dialogError}
        isSubmitting={isSubmittingAction}
        dataCy="sah-budget-elect-to-return-confirm-dialog"
        onClose={closeActionDialog}
        onConfirm={handleConfirmAction}
      />
    </>
  );
};

export default BudgetsModal;
